import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Module-scope flag: ensures the haptic ping fires AT MOST once per browser
// session (survives StrictMode double-mounts since the variable lives on the
// module, not the component instance). Resets on a real full reload.
let HAS_PINGED_THIS_SESSION = false;

/**
 * Mobile-only sticky pill that mirrors the right-rail LockupNudge. Sits just
 * above the BottomNav (which is ~66px tall + safe-area) and is dismissible
 * for the current session. Hidden when the user has no remaining locked CAUL
 * or when they're on a route where right-rails already cover the message.
 *
 * Motion: a soft 420ms slide-up + fade on mount (cubic-bezier ease-out) plus a
 * continuous ember-glow pulse on the lock icon. The entrance plays on every
 * fresh mount of /feed — subtle enough not to feel pushy.
 *
 * Haptics: a single 15ms `navigator.vibrate` ping fires the very first time
 * the pill becomes visible in a session (on devices that support vibration).
 * Gracefully degrades to a no-op when unsupported.
 */
export default function MobileLockupPill() {
  const { user } = useAuth();
  const loc = useLocation();
  const [lockups, setLockups] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Scope to the Feed route only — that's where the desktop nudge lives.
  const onFeed = loc.pathname === "/feed" || loc.pathname.startsWith("/feed/");

  useEffect(() => {
    try {
      if (sessionStorage.getItem("cauldron.lockupPill.dismissed") === "1") {
        setDismissed(true);
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!user || !onFeed) return;
    let cancelled = false;
    api
      .get("/rewards/lockups")
      .then(({ data }) => { if (!cancelled) setLockups(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, onFeed]);

  // First-ever visible appearance in this session → soft haptic ping (15ms).
  // Stays a no-op on devices without Vibration API support (iOS Safari, most
  // desktops). Guarded by a module-level flag so StrictMode's double-mount
  // doesn't double-tap. Fires from useEffect to keep render pure.
  const shouldRender = Boolean(
    user && onFeed && !dismissed && lockups && lockups.stillLocked > 0
  );
  useEffect(() => {
    if (!shouldRender || HAS_PINGED_THIS_SESSION) return;
    HAS_PINGED_THIS_SESSION = true;
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(15);
      }
    } catch { /* noop */ }
  }, [shouldRender]);

  if (!shouldRender) return null;

  const still = lockups.stillLocked;
  const total = lockups.totalLocked || 0;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem("cauldron.lockupPill.dismissed", "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="lg:hidden fixed left-0 right-0 z-40 px-3 pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)" }}
      data-testid="mobile-lockup-pill-wrap"
    >
      <Link
        to="/wallet"
        className="pointer-events-auto flex items-center gap-3 rounded-full pl-3 pr-2 py-2 shadow-lg backdrop-blur border border-primary/40 bg-background/95 hover:bg-background lockup-pill-in"
        data-testid="mobile-lockup-pill"
      >
        <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0 lockup-ember-glow">
          <Lock size={13} className="text-primary" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <div className="text-[11px] uppercase tracking-[0.16em] text-primary/80">
            Bonus CAUL Locked
          </div>
          <div className="text-xs text-foreground/85 truncate">
            Next paid tx unlocks <b className="text-secondary">100 CAUL</b>
            <span className="text-foreground/45"> · {still}/{total}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="w-7 h-7 rounded-full text-foreground/55 hover:text-foreground hover:bg-foreground/5 flex items-center justify-center shrink-0"
          data-testid="mobile-lockup-pill-dismiss"
        >
          <X size={14} strokeWidth={2} />
        </button>
      </Link>
    </div>
  );
}
